import { useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import { useProgressBar } from '@hooks/helpers/useProgressBar';
import CardTypeSection from '@sections/order_card/card_type';
import ProgressBar from '@components/progress_bar';
import Button from '@components/common/button';
import SuccessStep from '@sections/order_card/success';
import { routes } from '@const/routes';
import { useTranslation } from 'react-i18next';
import { useForm } from '@hooks/helpers/useForm';
import { addressValidationErrors, defaultRequestCard } from '@const/default_form_data';
import { zodResolver } from '@hookform/resolvers/zod';
import { requestCardValidation } from '@helpers/validation_objects/cards';
import { useLocales } from '@hooks/helpers/useLocales';
import { FormProvider, useWatch } from 'react-hook-form';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiDocumentService, apiUserService, apiWalletService } from '@api';
import { useUserContext } from '@hooks/context/useUserContext';
import { useVerificationGuard } from '@hooks/helpers/useVerificationGuard';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { ECardType } from '@xeppt/xeppt-sdk/types/card';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { Country } from 'country-state-city';
import CardDetails from '@sections/order_card/card_details';
import ShippingAddress from '@sections/order_card/address';
import CardHolder from '@sections/order_card/cardholder';
import Agreement from '@sections/order_card/agreement';
import { useNavigationContext } from '@hooks/context/useNavigationContext';

const steps = [
    {
        title: 'Card details',
        id: '1'
    },
    {
        title: 'Shipping address',
        id: '2'
    },
    {
        title: 'Cardholder info',
        id: '3'
    },
    {
        title: 'Agreement',
        id: '4'
    },
    {
        title: 'Done',
        id: '5'
    }
];

const OrderCardLayout = () => {
    const { t } = useTranslation('order_card');
    const { validationLocale, requestSuccessLocale, requestErrorLocale, submitLocale } =
        useLocales();
    const { user, refetchAccount } = useUserContext();
    const stepsMemo = useMemo(() => steps, [t]);
    const { currentStep, availableStep, changeStep, prevStep, nextStep } =
        useProgressBar(stepsMemo);
    const { verifiedAction } = useVerificationGuard();
    const [isDone, setIsDone] = useState(false);
    const [cardType, setCardType] = useState<ECardType>();
    const { handleBack } = useNavigationContext();
    const form = useForm({
        defaultValues: defaultRequestCard,
        resolver: zodResolver(
            requestCardValidation({
                currentStep: Number(currentStep?.id || 1) - 1,
                messages: {
                    shipping_address: addressValidationErrors(validationLocale),
                    first_name: validationLocale('first_name'),
                    last_name: validationLocale('last_name'),
                    billing_address: addressValidationErrors(validationLocale),
                    employer_address: addressValidationErrors(validationLocale),
                    birth_date: validationLocale('birth_date'),
                    occupation: validationLocale('occupation'),
                    employerName: validationLocale('employerName'),
                    employerPhone: validationLocale('employerPhone'),
                    cell_phone: validationLocale('cell_phone'),
                    citizenship: validationLocale('citizenship'),
                    email: validationLocale('email'),
                    status: validationLocale('citizenshipStatus')
                },
                cardType
            })
        )
    });
    const watchedAgreement = useWatch({ name: 'agree', control: form.control });

    const { data: cards, isLoading: isCardsLoading } = useApiQuery({
        method: () => apiWalletService.getCards(),
        isInitialRequest: false
    });

    const { data: addressData } = useApiQuery({
        method: () => apiUserService.getAddress()
    });

    useEffect(() => {
        form.reset({
            ...defaultRequestCard,
            first_name: user?.profile.first_name,
            last_name: user?.profile.last_name,
            email: user?.email,
            //@ts-ignore
            birth_date: new Date(user?.profile?.birth_date),
            cell_phone: `${user?.phone_number}`,
            //@ts-ignore
            occupation: user?.profile?.occupancy,
            //@ts-ignore
            shipping_address: addressData?.billing_address
                ? {
                      //@ts-ignore
                      ...addressData?.billing_address,
                      //@ts-ignore
                      address_2: addressData?.billing_address.address_2 || '',
                      country:
                          Country.getAllCountries().find(
                              //@ts-ignore
                              (item) => item.isoCode === addressData?.billing_address?.country
                              //@ts-ignore
                          )?.name || addressData?.billing_address?.country
                  }
                : defaultRequestCard.shipping_address
        });
    }, [user, addressData]);

    // Fetch the active card agreement on mount: its id is required by the
    // backend (agreement_document_id) when ordering a card. Previously this was
    // isInitialRequest:false and never triggered, so agreement.id was empty and
    // POST /wallet/card failed with 400 agreement_document_id:required.
    const { data: agreement } = useApiQuery({
        method: () => apiDocumentService.GetDocumentByName('card_agreement')
    });

    const getAgreementInfo = () => {
        const values = form.getValues();
        return {
            agreement_document_id: agreement?.id || '',
            agreement_accepted_at: new Date(),
            agreement_details: {
                first_name: values.first_name,
                last_name: values.last_name,
                middle_name: values.middle_name,
                birth_date: values.birth_date,
                alias: values.alias,
                citizenship: values.citizenship,
                home_phone: values.phone_number?.startsWith('1')
                    ? values.phone_number
                    : `1${values.phone_number}`,
                mobile_phone: values.cell_phone?.startsWith('1')
                    ? values.cell_phone
                    : `1${values.cell_phone}`,
                email: values.email,
                status: values.status,
                occupation: values.occupation,
                permanent_residence: {
                    ...values.permanent_residence,
                    country:
                        Country.getAllCountries().find(
                            (item) => item.name === values.permanent_residence.country
                        )?.isoCode || values.permanent_residence.country
                },
                tax_info: values.tax_info
                    .map((item) => {
                        return {
                            country: item.country,
                            tin: item.tin,
                            reason: item.reason_id !== 'Other' ? item.reason_id : item.reason
                        };
                    })
                    .filter((item) => {
                        if (item.country && (item.tin || item.reason)) {
                            return true;
                        }
                        return false;
                    })
            }
        };
    };

    const { handleRequest: handleRequestCard, isLoading: isRequestCardLoading } = useApiMutation({
        method: () =>
            apiWalletService.requestPhysicCard({
                profile: {
                    first_name: form.getValues().first_name || '',
                    last_name: form.getValues().last_name || ''
                },
                shipping_address: {
                    ...form.getValues().shipping_address,
                    country:
                        Country.getAllCountries().find(
                            (item) => item.name === form.getValues().shipping_address.country
                        )?.isoCode || form.getValues().shipping_address.country
                },
                ...getAgreementInfo()
            }),
        onSuccess: () => {
            requestSuccessLocale('request_card');
            refetchAccount();
            nextStep();
            setIsDone(true);
        },
        onError: (e) => {
            setIsDone(false);
            requestErrorLocale(e);
        }
    });

    const {
        handleRequest: handleRequestAnotherCard,
        isLoading: isRequestSupplementaryCardLoading
    } = useApiMutation({
        method: () => {
            const values = form.getValues();
            return apiWalletService.requestSupplementaryCard({
                //@ts-ignore
                card_id: values?.card_id || '',
                profile: {
                    first_name: values.first_name || '',
                    last_name: values.last_name || ''
                },
                shipping_address: {
                    ...values.shipping_address,
                    country:
                        Country.getAllCountries().find(
                            (item) => item.name === values.shipping_address.country
                        )?.isoCode || values.shipping_address.country
                },
                ...getAgreementInfo()
            });
        },
        onSuccess: () => {
            requestSuccessLocale('request_card');
            refetchAccount();
            nextStep();
            setIsDone(true);
        },
        onError: (e) => {
            setIsDone(false);
            requestErrorLocale(e);
        }
    });

    const renderStep = () => {
        switch (currentStep?.id) {
            case '1':
                return <CardDetails type={cardType} cards={cards || []} />;
            case '2':
                return <ShippingAddress />;
            case '3':
                return <CardHolder />;
            case '4':
                return <Agreement agreement={agreement} />;
            default:
                return <SuccessStep cardType={cardType} />;
        }
    };

    const renderTitle = () => {
        if (!cardType) {
            return t('choose_card');
        }
        return cardType === ECardType.PHYSICAL_SUPPLEMENTARY
            ? 'Order a supplementary card'
            : 'Order a primary card';
    };

    const onSubmit = () => {
        verifiedAction(() => {
            form.trigger().then((isValid) => {
                if (isValid) {
                    if (currentStep?.id !== '4') {
                        nextStep();
                    } else {
                        cardType === ECardType.PHYSICAL_SUPPLEMENTARY &&
                            handleRequestAnotherCard(undefined);
                        cardType === ECardType.PHYSICAL && handleRequestCard(undefined);
                    }
                }
            });
        });
    };

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    leftIcon="arrow_left"
                    variant="text-gray"
                    onClick={() => {
                        if (cardType) {
                            form.reset({
                                ...defaultRequestCard,
                                first_name: user?.profile.first_name,
                                last_name: user?.profile.last_name
                            });
                            setCardType(undefined);
                            changeStep('1');
                        } else {
                            handleBack(routes.dashboard);
                        }
                    }}>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4" dangerouslySetInnerHTML={{ __html: t(renderTitle()) }} />
            </div>
            <div className={styles.content_wrapper}>
                {!cardType ? (
                    <CardTypeSection
                        isCardsLoading={isCardsLoading}
                        cards={cards}
                        onChange={(value) => setCardType(value)}
                    />
                ) : (
                    <>
                        <ProgressBar
                            steps={steps}
                            availableStep={availableStep}
                            currentStep={currentStep}
                            className={styles.progress}
                            changeStep={changeStep}
                        />
                        <div
                            className={`${styles.body_wrapper} ${currentStep?.id === '4' ? styles.large : ''}`}>
                            <FormProvider {...form}>{renderStep()}</FormProvider>
                        </div>
                    </>
                )}
            </div>
            {cardType && currentStep?.id !== '5' && (
                <div className={styles.actions_wrapper}>
                    {currentStep?.id !== '1' && (
                        <Button
                            className={styles.back}
                            leftIcon="arrow_left"
                            variant="outlined-primary"
                            onClick={() => {
                                prevStep();
                            }}>
                            {submitLocale('previous')}
                        </Button>
                    )}
                    <Button
                        variant="primary"
                        rightIcon="arrow_right"
                        disabled={!watchedAgreement && currentStep?.id === '4'}
                        isLoading={isRequestSupplementaryCardLoading || isRequestCardLoading}
                        onClick={onSubmit}>
                        {isDone ? t('done') : t('next')}
                    </Button>
                </div>
            )}
        </motion.div>
    );
};

export default OrderCardLayout;
